import * as firebase_auth from "firebase/auth";
import {
  UserLookupResult,
  userLookupService,
} from "../../services/userLookupService";
import { useThenOf } from "../../utils";
import { locale, translation } from "../../translations";
import styled, { keyframes } from "styled-components";
import React, { useState } from "react";
import {
  IResolveParams,
  LoginSocialApple,
  LoginSocialFacebook,
} from "reactjs-social-login";
import {
  AppleLoginButton,
  FacebookLoginButton,
  SpecificSocialLoginButtonProps,
} from "react-social-login-buttons";
import Typography from "@mui/material/Typography";
import { GoogleLogin } from '@react-oauth/google';
import GoogleAuthProvider = firebase_auth.GoogleAuthProvider;
import FacebookAuthProvider = firebase_auth.FacebookAuthProvider;
import OAuthProvider = firebase_auth.OAuthProvider;
import { Box } from "@mui/material";
import * as Sentry from "@sentry/react";

const appleAuthProvider = new OAuthProvider("apple.com");

const facebookAppId = "387579481974810";
const appleClientId = "life.vika";

const fadeIn = keyframes`
  to {
    opacity: 1;
  }
`;

const Wrapper = styled.div`
  // Cover the layout glitches caused by the Google button with an animation
  opacity: 0;
  animation-name: ${fadeIn};
  animation-duration: 300ms;
  animation-fill-mode: forwards;
  animation-delay: 500ms;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const ProviderList = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ButtonWrapper = styled.div`
  margin: 5px;
`;

const ChooseProviderStateView = (props: {
  readonly onProviderAuthenticated: (
    authCredential: firebase_auth.AuthCredential,
  ) => void;
}) => {
  const redirectUri = window.location.href;

  const socialLoginButtonProps: SpecificSocialLoginButtonProps = {
    iconSize: "16px",
    size: "32px",
    align: "center",
    style: {
      fontSize: "14px",
      boxShadow: "none",
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor: "#D3D3D3",
      borderRadius: "4px",
    },
  };

  return (
    <Wrapper>
      <Typography align="center" variant="h6">
        {translation.useSignUpProvider}
      </Typography>
      <ProviderList>
        <LoginSocialFacebook
          appId={facebookAppId}
          redirect_uri={redirectUri}
          fieldsProfile={"id"}
          onResolve={({ data }: IResolveParams) => {
            const accessToken: string = data!["accessToken"];
            props.onProviderAuthenticated(
              FacebookAuthProvider.credential(accessToken),
            );
          }}
          onReject={(err) => {
            Sentry.captureException(err);
          }}
        >
          <FacebookLoginButton
            {...socialLoginButtonProps}
            text={translation.signInWithFacebook}
          />
        </LoginSocialFacebook>

        <ButtonWrapper>
          <GoogleLogin
            size={"medium"}
            text={"signin_with"}
            width={240}
            locale={locale}
            onSuccess={credentialResponse => {
              const oAuthCredential = GoogleAuthProvider.credential(
                credentialResponse.credential,
              );
              props.onProviderAuthenticated(oAuthCredential);
            }}
          />
        </ButtonWrapper>

        <LoginSocialApple
          client_id={appleClientId}
          scope={"name email"}
          redirect_uri={redirectUri}
          onResolve={({ data }: IResolveParams) => {
            const authorization = data!["authorization"];
            const idToken: string = authorization["id_token"];
            const code: string = authorization["code"];

            props.onProviderAuthenticated(
              appleAuthProvider.credential({
                idToken,
                accessToken: code,
              }),
            );
          }}
          onReject={(err) => {
            Sentry.captureException(err);
          }}
        >
          <AppleLoginButton
            {...socialLoginButtonProps}
            text={translation.signInWithApple}
          />
        </LoginSocialApple>
      </ProviderList>
    </Wrapper>
  );
};

const SigningInStateView = (props: {
  readonly authCredential: firebase_auth.AuthCredential;
  readonly onSuccess: (userLookupResult: UserLookupResult) => void;
  readonly onError: () => void;
}) => {
  useThenOf(() => userLookupService.lookupUser(props.authCredential), {
    onFulfilled: props.onSuccess,
    onRejected: (err) => {
      // TODO: Use Sentry
      console.error("Error in signInWithCredential", err);
      props.onError();
    },
  });

  return <Typography>{translation.signingIn}</Typography>;
};

export const SignInStepView = (props: {
  readonly onAuthenticated: (userCredential: UserLookupResult) => void;
}) => {
  const buildChooseProviderView = () => (
    <ChooseProviderStateView
      onProviderAuthenticated={(authCredential) => {
        setStateView(
          <SigningInStateView
            authCredential={authCredential}
            onSuccess={(userLookupResult) => {
              props.onAuthenticated(userLookupResult);
            }}
            onError={() => {
              setStateView(buildChooseProviderView());
            }}
          />,
        );
      }}
    />
  );

  const [stateView, setStateView] = useState(buildChooseProviderView());

  return (
    <Box
      style={{
        display: "flex",
        justifyContent: "center",
      }}
    >
      {stateView}
    </Box>
  );
};
